import { create } from "zustand";
import { useSwap } from "./swap.store";
import { usePools } from "./pools.store";
import { AviproductionPairApi, IPairInfoResponse } from "@api/AviproductionPairApi";

// Interface for the pair store
interface IPairStore {
    // State
    currentPairInfo: IPairInfoResponse | null;
    isLoading: boolean;
    error: string | null;

    // Actions
    fetchPairInfo: (poolAddress?: string) => Promise<void>;
    fetchPairUsdPriceInfo: () => { sellAssetUsdPrice: number | null; buyAssetUsdPrice: number | null };
    reset: () => void;
}

export const usePair = create<IPairStore>()((set, get) => ({
    // Initial state
    currentPairInfo: null,
    isLoading: false,
    error: null,

    // Fetch pair information based on the provided pool address or find a matching pool
    fetchPairInfo: async (providedPoolAddress?: string) => {
        // Get current assets from swap store (only needed if no pool address is provided)
        const { sellAssetInfo, buyAssetInfo } = useSwap.getState();

        // Get pools from pools store (only needed if no pool address is provided)
        const { pools } = usePools.getState();

        // Determine the pool address to use
        let poolAddress = providedPoolAddress || "";

        // If no pool address was provided, try to find a matching pool based on assets
        if (!poolAddress) {
            // Validate we have the necessary assets data
            if (!sellAssetInfo.asset || !buyAssetInfo.asset) {
                set({ error: "Missing assets for pair info", isLoading: false });
                return;
            }

            // Find a matching pool based on the assets
            const matchingPool = pools.find(
                (pool) =>
                    (pool.assetInJettonMasterAddress === sellAssetInfo.asset.jettonMasterAddress &&
                        pool.assetOutJettonMasterAddress === buyAssetInfo.asset.jettonMasterAddress) ||
                    (pool.assetInJettonMasterAddress === buyAssetInfo.asset.jettonMasterAddress &&
                        pool.assetOutJettonMasterAddress === sellAssetInfo.asset.jettonMasterAddress),
            );

            if (matchingPool) {
                poolAddress = matchingPool.poolAddress;
            } else {
                set({ error: "No matching pool found for assets", isLoading: false });
                return;
            }
        }

        // If we couldn't determine a pool address, return an error
        if (!poolAddress) {
            set({
                error: "No pool address provided or found",
                isLoading: false,
                currentPairInfo: null,
            });
            return;
        }

        // Set loading state
        set({ isLoading: true, error: null });

        try {
            // Make the API request
            const pairInfo = await AviproductionPairApi.getPairPairInfo({
                address: poolAddress,
                network: "ton",
            });

            // Update state with the result
            if (pairInfo) {
                set({
                    currentPairInfo: pairInfo,
                    isLoading: false,
                    error: null,
                });
            } else {
                set({
                    error: "Failed to fetch pair information",
                    isLoading: false,
                    currentPairInfo: null,
                });
            }
        } catch (error) {
            set({
                error: error instanceof Error ? error.message : "Unknown error fetching pair info",
                isLoading: false,
                currentPairInfo: null,
            });
        }
    },

    // Get USD prices for sell and buy assets from the current pair info
    fetchPairUsdPriceInfo: () => {
        const { currentPairInfo } = get();
        const { sellAssetInfo, buyAssetInfo } = useSwap.getState();

        // Default values if prices can't be determined
        let sellAssetUsdPrice: number | null = null;
        let buyAssetUsdPrice: number | null = null;

        // If we have pair info and tokens
        if (currentPairInfo && currentPairInfo.tokens && currentPairInfo.tokens.length >= 2) {
            // Try to match tokens with sell and buy assets
            if (sellAssetInfo.asset && buyAssetInfo.asset) {
                // Special handling for TON (native token)
                if (
                    sellAssetInfo.asset.type === "ton" ||
                    sellAssetInfo.asset.type === "native"
                ) {
                    // Find TON token - robustly check for is_native, then fallback to symbol/address
                    const tonToken = currentPairInfo.tokens.find(
                        (token) =>
                            token.is_native === true ||
                            token.symbol?.toLowerCase() === "ton" ||
                            token.address?.toLowerCase() === "ton"
                    );

                    if (tonToken) {
                        sellAssetUsdPrice = tonToken.price_usd;
                    }
                } else {
                    // Find the token that matches the sell asset
                    const sellToken = currentPairInfo.tokens.find(
                        (token) =>
                            token.address?.toLowerCase() === sellAssetInfo.asset.jettonMasterAddress?.toLowerCase(),
                    );

                    if (sellToken) {
                        sellAssetUsdPrice = sellToken.price_usd;
                    }
                }

                // Similar special handling for TON as buy asset
                if (
                    buyAssetInfo.asset.type === "ton" ||
                    buyAssetInfo.asset.type === "native"
                ) {
                    const tonToken = currentPairInfo.tokens.find(
                        (token) =>
                            token.is_native === true ||
                            token.symbol?.toLowerCase() === "ton" ||
                            token.address?.toLowerCase() === "ton"
                    );

                    if (tonToken) {
                        buyAssetUsdPrice = tonToken.price_usd;
                    }
                } else {
                    // Find the token that matches the buy asset
                    const buyToken = currentPairInfo.tokens.find(
                        (token) =>
                            token.address?.toLowerCase() === buyAssetInfo.asset.jettonMasterAddress?.toLowerCase(),
                    );

                    if (buyToken) {
                        buyAssetUsdPrice = buyToken.price_usd;
                    }
                }
            }
        }

        return { sellAssetUsdPrice, buyAssetUsdPrice };
    },

    // Reset the store state
    reset: () => {
        set({
            currentPairInfo: null,
            isLoading: false,
            error: null,
        });
    },
}));

// Subscribe to changes in the swap and pools stores to automatically update pair info
// This ensures the pair info is always in sync with the selected assets and pool
export const setupPairSubscriptions = () => {
    // Subscribe to swap store changes
    useSwap.subscribe((state, prevState) => {
        // Check if the assets have changed
        if (
            state.sellAssetInfo?.asset?.guid !== prevState.sellAssetInfo?.asset?.guid ||
            state.buyAssetInfo?.asset?.guid !== prevState.buyAssetInfo?.asset?.guid ||
            state.selectedPool !== prevState.selectedPool
        ) {
            // If assets or selected pool changed, fetch new pair info
            // If a pool is selected, use its address for more accurate pair info
            if (state.selectedPool) {
                const poolData = usePools.getState().pools.find((p) => p.dex === state.selectedPool);
                if (poolData) {
                    usePair.getState().fetchPairInfo(poolData.poolAddress);
                    return;
                }
            }

            // If no pool is selected or found, fetch without specifying a pool address
            usePair.getState().fetchPairInfo();
        }
    });

    // Subscribe to pools store changes
    usePools.subscribe((state, prevState) => {
        // Only update if pools were loaded and we previously had none
        if (state.pools.length > 0 && prevState.pools.length === 0) {
            // If new pools were loaded, fetch new pair info (without specifying a pool address)
            usePair.getState().fetchPairInfo();
        }
    });
};
